import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { Chip, DataRow } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BankIcon,
  CheckmarkCircle02Icon,
  CreditCardIcon,
  PlusSignIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { titleCase } from "title-case";
import { GET_BALANCE_SETTINGS } from "graphql/company";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import {
  useResolvedBankAccounts,
  UseGetBankAccountsResult,
  BankAccount,
} from "hooks/useGetBankAccounts";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";
import AccountActionsMenu, {
  AccountDrawer,
} from "components/admin/bank/AccountActionsMenu";
import RecentTransactions from "components/common/transactions/RecentTransactions";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";

interface AccountsPanelProps {
  // Provided by the dashboard (single shared fetch); self-fetches when absent.
  bankAccounts?: UseGetBankAccountsResult;
}

const accountLabel = (account: BankAccount, fallback: string): string => {
  const name = account.nickname || account.bankName;
  return name ? titleCase(name.toLowerCase()) : fallback;
};

/**
 * The company's own accounts, the actions that move money through them, and the
 * transactions that just did.
 *
 * One panel for both programs. A charge account and a prepaid one differ in
 * what their row's value means (credit available against cash held) and in
 * which actions the overflow menu offers, which is not enough to justify the
 * two near-identical panels this replaced.
 */
const AccountsPanel: React.FC<AccountsPanelProps> = ({ bankAccounts }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [transactionId, setTransactionId] = useState("");
  const [requestedDrawer, setRequestedDrawer] = useState<AccountDrawer | null>(
    null,
  );

  const {
    internalAccounts,
    isLoading: loadingAccounts,
    refetchAllAccounts,
  } = useResolvedBankAccounts(bankAccounts, {
    accountTypes: [AccountType.FINANCIAL_ACCOUNT],
  });

  const { data: balanceSettings, refetch: refetchBalanceSettings } = useQuery(
    GET_BALANCE_SETTINGS,
    { fetchPolicy: "network-only" },
  );

  const targetBalance = balanceSettings?.getBalanceSettings?.TARGET_BALANCE;
  const autoBalanceOn =
    !!targetBalance?.toAccountId && !targetBalance?.isDisabled;

  const charge = useMemo(
    () =>
      internalAccounts.some(
        (account) => account.applicationType === ApplicationType.CHARGE,
      ),
    [internalAccounts],
  );

  const refreshBankAndBalance = () => {
    refetchAllAccounts();
    refetchBalanceSettings();
  };

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        height: "100%",
        padding: "var(--space-6)",
        gap: "var(--space-2)",
      }}
    >
      <Inline justify="between" wrap={false}>
        <Typography variant="h4" component="h3">
          {t("Accounts")}
        </Typography>
        <AccountActionsMenu
          charge={charge}
          requested={requestedDrawer}
          onRequestedClose={() => setRequestedDrawer(null)}
          refreshTransactions={refreshBankAndBalance}
          bankAccounts={bankAccounts}
        />
      </Inline>

      <Stack gap="var(--space-4)">
        {loadingAccounts && internalAccounts.length === 0 ? (
          <CustomSkeleton variant="list" rows={2} hideCircular />
        ) : (
          internalAccounts.map((account) => (
            <DataRow
              key={account.id}
              type="amount"
              icon={{
                glyph: (
                  <HugeiconsIcon
                    icon={charge ? CreditCardIcon : BankIcon}
                    size={18}
                  />
                ),
                tone: "brand",
              }}
              title={accountLabel(account, t("Account"))}
              tag={
                autoBalanceOn && targetBalance?.toAccountId === account.id ? (
                  <Chip
                    size="sm"
                    tone="neutral"
                    leading={
                      <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} />
                    }
                    label={t("Auto balance")}
                  />
                ) : undefined
              }
              subtitle={account.last4 ? `•••• ${account.last4}` : undefined}
              amount={account.availableAmount}
              onClick={() =>
                navigate("/admin/transactions", {
                  state: { accountId: account.id },
                })
              }
            />
          ))
        )}

        {/* The row keeps the button at its intrinsic width: `Stack` stretches. */}
        <Inline>
          <Button
            variant="primary-soft"
            size="small"
            startIcon={<HugeiconsIcon icon={PlusSignIcon} size={16} />}
            onClick={() => setRequestedDrawer("connectedAccounts")}
          >
            {t("Add account")}
          </Button>
        </Inline>
      </Stack>

      <Divider sx={{ margin: "var(--space-3) 0" }} />

      <RecentTransactions scope="company" onRowClick={setTransactionId} />

      <TransactionDetailsDrawer
        open={!!transactionId}
        handleCloseDrawer={() => setTransactionId("")}
        transactionId={transactionId}
      />
    </Card>
  );
};

export default AccountsPanel;
